/**
 * Utility for determining the start date of the next incremental data request.
 */

// -- imports --

import type { CurveId } from '../config/plotTypes';
import type { PlotDataInput } from './PlotDataInput';

// -- type definitions --

export interface FindNextRequestIntervalStartOptions {
  maxLookbackMs?: number;
  maxStartDate?: Date;
}

type PlotEntry = [Date, Record<string, number>];

// -- constants --

const DEFAULT_MAX_LOOKBACK_MS = 5 * 60 * 1000;

// -- functions --

function sortEntriesByDateDescending(data: PlotDataInput): PlotEntry[] {
  return [...data.entries()].sort(
    ([leftDate], [rightDate]) => rightDate.getTime() - leftDate.getTime()
  );
}

function isNonNullCurveValue(value: number | undefined): boolean {
  return value !== undefined && Number.isFinite(value);
}

/**
 * Determines the best start date for the next incremental fetch based on the
 * latest non-null values found for the requested curves.
 *
 * - scans the plot data backwards
 * - looks for the most recent timestamp containing a usable value for each requested curve
 * - returns the oldest timestamp among the collected matches
 * - if no usable values are found, returns the most recent timestamp in the data
 *
 * @param data Formatted plot data keyed by timestamp.
 * @param curveIds Curve identifiers to inspect.
 * @param options Optional search configuration.
 * @param options.maxStartDate Optional upper bound for the returned start date.
 * @returns The start date to use for the next request, or undefined when there is no data.
 *
 * @example
 * const startDate = findNextRequestIntervalStart(data, ['temperature', 'humidity']);
 */
export function findNextRequestIntervalStart(
  data: PlotDataInput,
  curveIds: readonly CurveId[],
  options: FindNextRequestIntervalStartOptions = {}
): Date | undefined {
  const entries = sortEntriesByDateDescending(data);
  const latestDate = entries[0]?.[0];

  if (!latestDate) {
    return undefined;
  }

  const maxLookbackMs = Math.max(
    options.maxLookbackMs ?? DEFAULT_MAX_LOOKBACK_MS,
    0
  );
  const cutoffMs = latestDate.getTime() - maxLookbackMs;
  const unresolvedCurveIds = new Set(curveIds);
  const resolvedDates = new Map<CurveId, Date>();

  for (const [date, values] of entries) {
    if (unresolvedCurveIds.size === 0) {
      break;
    }

    if (date.getTime() < cutoffMs) {
      break;
    }

    const newlyResolvedCurveIds: CurveId[] = [];

    for (const curveId of unresolvedCurveIds) {
      if (isNonNullCurveValue(values[curveId])) {
        resolvedDates.set(curveId, date);
        newlyResolvedCurveIds.push(curveId);
      }
    }

    for (const curveId of newlyResolvedCurveIds) {
      unresolvedCurveIds.delete(curveId);
    }
  }

  const candidateDate =
    resolvedDates.size === 0
      ? latestDate
      : new Date(
          Math.min(...[...resolvedDates.values()].map((date) => date.getTime()))
        );

  const maxStartDateMs = options.maxStartDate?.getTime();

  if (
    maxStartDateMs !== undefined &&
    !Number.isNaN(maxStartDateMs) &&
    candidateDate.getTime() > maxStartDateMs
  ) {
    return new Date(maxStartDateMs);
  }

  return candidateDate;
}
